/* ==========================================================================
   FILE: api/client.js
   IAMONEAI - Core API Client
   High-performance fetch wrapper with caching, retry, and offline support
   ========================================================================== */

import { cache } from './cache';
import { queue } from './queue';

// ==========================================================================
// CONFIGURATION
// ==========================================================================

const CONFIG = {
  baseURL: 'https://api.iamoneai.com',
  apiKey: 'hpXaxXVSYu7lyC3XkpHx35wl5ZagFCv9wXY1eggi',
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000, // Base delay, doubles each attempt
};

// ==========================================================================
// REQUEST DEDUPLICATION
// ==========================================================================

// Prevents duplicate simultaneous requests to same endpoint
const pendingRequests = new Map();

function getRequestKey(method, endpoint, body) {
  return `${method}:${endpoint}:${JSON.stringify(body || '')}`;
}

// ==========================================================================
// ONLINE STATUS
// ==========================================================================

let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    isOnline = true;
    // Trigger queue flush when back online
    queue.flush().catch(console.error);
  });
  window.addEventListener('offline', () => {
    isOnline = false;
  });
}

// ==========================================================================
// CORE FETCH WRAPPER
// ==========================================================================

/**
 * Make an API request with caching, retry, and offline support
 * 
 * @param {string} endpoint - API endpoint (e.g., '/brain/20AA-...')
 * @param {Object} options - Request options
 * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object} options.body - Request body (will be JSON stringified)
 * @param {Object} options.headers - Additional headers
 * @param {boolean|string} options.cache - Cache behavior: true, false, 'force'
 * @param {number} options.cacheTTL - Cache TTL in milliseconds
 * @param {boolean} options.retry - Enable retry on failure
 * @param {string} options.offline - Offline behavior: 'queue', 'fail', 'cache'
 * @param {string} options.priority - Queue priority: 'high', 'normal', 'low'
 * @returns {Promise<any>} Response data
 */
async function request(endpoint, options = {}) {
  const {
    method = 'GET',
    body = null,
    headers = {},
    cache: useCache = method === 'GET',
    cacheTTL = 300000, // 5 minutes default
    retry = true,
    offline = 'queue',
    priority = 'normal',
  } = options;

  const url = `${CONFIG.baseURL}${endpoint}`;
  const requestKey = getRequestKey(method, endpoint, body);

  // ==========================================================================
  // CHECK CACHE (GET requests only)
  // ==========================================================================

  if (useCache && method === 'GET') {
    const cached = cache.get(requestKey);
    if (cached !== null) {
      return cached;
    }
  }

  // ==========================================================================
  // CHECK FOR PENDING DUPLICATE REQUEST
  // ==========================================================================

  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }

  // ==========================================================================
  // CHECK OFFLINE STATUS
  // ==========================================================================

  if (!isOnline) {
    if (offline === 'queue' && method !== 'GET') {
      // Queue for later
      const queueId = await queue.add({
        endpoint,
        method,
        body,
        priority,
        timestamp: Date.now(),
      });
      return { queued: true, queueId, offline: true };
    } else if (offline === 'cache' && method === 'GET') {
      // Return stale cache if available
      const stale = cache.get(requestKey, { allowStale: true });
      if (stale !== null) {
        return { ...stale, stale: true };
      }
    }
    throw new Error('You are offline. Please check your connection.');
  }

  // ==========================================================================
  // BUILD REQUEST
  // ==========================================================================

  const fetchOptions = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': CONFIG.apiKey,
      ...headers,
    },
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  // ==========================================================================
  // EXECUTE WITH RETRY
  // ==========================================================================

  const executeRequest = async (attempt = 1) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CONFIG.timeout);
    fetchOptions.signal = controller.signal;

    try {
      const startTime = performance.now();
      const response = await fetch(url, fetchOptions);
      const duration = performance.now() - startTime;

      clearTimeout(timeoutId);

      // Parse response
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Handle errors
      if (!response.ok) {
        const error = new Error(data?.message || data?.error || `HTTP ${response.status}`);
        error.status = response.status;
        error.data = data;
        throw error;
      }

      // Add timing metadata
      if (typeof data === 'object' && data !== null) {
        data._meta = {
          duration,
          cached: false,
          timestamp: Date.now(),
        };
      }

      return data;

    } catch (err) {
      clearTimeout(timeoutId);

      // Don't retry on client errors (4xx) or abort
      if (err.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      if (err.status && err.status >= 400 && err.status < 500) {
        throw err;
      }

      // Retry on network/server errors
      if (retry && attempt < CONFIG.retryAttempts) {
        const delay = CONFIG.retryDelay * Math.pow(2, attempt - 1);
        await sleep(delay);
        return executeRequest(attempt + 1);
      }

      throw err;
    }
  };

  // ==========================================================================
  // EXECUTE AND CACHE
  // ==========================================================================

  const requestPromise = executeRequest()
    .then((data) => {
      // Cache successful GET requests
      if (useCache && method === 'GET') {
        cache.set(requestKey, data, cacheTTL);
      }
      // Invalidate cache on mutations
      if (method !== 'GET') {
        cache.invalidatePattern(new RegExp(endpoint.split('/')[1]));
      }
      return data;
    })
    .finally(() => {
      pendingRequests.delete(requestKey);
    });

  // Store pending request for deduplication
  pendingRequests.set(requestKey, requestPromise);

  return requestPromise;
}

// ==========================================================================
// CONVENIENCE METHODS
// ==========================================================================

const client = {
  /**
   * GET request
   * @param {string} endpoint 
   * @param {Object} options 
   */
  get(endpoint, options = {}) {
    return request(endpoint, { ...options, method: 'GET' });
  },

  /**
   * POST request
   * @param {string} endpoint 
   * @param {Object} data 
   * @param {Object} options 
   */
  post(endpoint, data, options = {}) {
    return request(endpoint, { ...options, method: 'POST', body: data });
  },

  /**
   * PUT request
   * @param {string} endpoint 
   * @param {Object} data 
   * @param {Object} options 
   */
  put(endpoint, data, options = {}) {
    return request(endpoint, { ...options, method: 'PUT', body: data });
  },

  /**
   * DELETE request
   * @param {string} endpoint 
   * @param {Object} options 
   */
  delete(endpoint, options = {}) {
    return request(endpoint, { ...options, method: 'DELETE' });
  },

  /**
   * Check if online
   */
  isOnline() {
    return isOnline;
  },

  /**
   * Get pending request count
   */
  getPendingCount() {
    return pendingRequests.size;
  },

  /**
   * Clear all caches
   */
  clearCache() {
    cache.clear();
  },

  /**
   * Get queue size
   */
  getQueueSize() {
    return queue.size();
  },

  /**
   * Flush offline queue
   */
  flushQueue() {
    return queue.flush();
  },
};

// ==========================================================================
// HELPERS
// ==========================================================================

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export { client, request, CONFIG };
export default client;